import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PrivacyPolicy = () => {
  const lastUpdated = "November 22, 2025";

  const sections = [
    {
      title: "Information We Collect",
      icon: "Database",
      content: [
        {
          subtitle: "Personal Information",
          items: [
            "Account information (name, email address, username)",
            "Profile data and preferences",
            "Payment information (processed securely through third-party providers)",
            "Communication history and support inquiries"
          ]
        },
        {
          subtitle: "Usage Data",
          items: [
            "3D model viewing history and interactions",
            "Search queries and browsing patterns",
            "Device information and browser type",
            "IP address and location data"
          ]
        },
        {
          subtitle: "Technical Data",
          items: [
            "Cookies and similar tracking technologies",
            "Session information and authentication tokens",
            "Error logs and performance metrics"
          ]
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: "Settings",
      content: [
        {
          subtitle: "Service Delivery",
          items: [
            "Provide and maintain our 3D model catalog services",
            "Process transactions and manage your account",
            "Deliver personalized recommendations",
            "Enable 3D model viewing and interaction features"
          ]
        },
        {
          subtitle: "Improvement and Analytics",
          items: [
            "Analyze usage patterns to improve our platform",
            "Develop new features and enhance user experience",
            "Monitor platform performance and security",
            "Conduct research and development"
          ]
        },
        {
          subtitle: "Communication",
          items: [
            "Send service updates and notifications",
            "Respond to support requests",
            "Share promotional content (with your consent)",
            "Provide important account information"
          ]
        }
      ]
    },
    {
      title: "Data Sharing and Disclosure",
      icon: "Share2",
      content: [
        {
          subtitle: "We share your information with:",
          items: [
            "Service providers who assist in platform operations",
            "Payment processors for transaction handling",
            "Analytics providers for usage insights",
            "Legal authorities when required by law"
          ]
        },
        {
          subtitle: "We never:",
          items: [
            "Sell your personal information to third parties",
            "Share data with advertisers without consent",
            "Use your data for purposes not disclosed in this policy"
          ]
        }
      ]
    },
    {
      title: "Your Rights and Choices",
      icon: "Shield",
      content: [
        {
          subtitle: "You have the right to:",
          items: [
            "Access your personal data we hold",
            "Request correction of inaccurate information",
            "Delete your account and associated data",
            "Export your data in a portable format",
            "Opt-out of marketing communications",
            "Withdraw consent for data processing"
          ]
        }
      ]
    },
    {
      title: "Data Security",
      icon: "Lock",
      content: [
        {
          subtitle: "Security Measures",
          items: [
            "Industry-standard encryption for data transmission",
            "Secure authentication and access controls",
            "Regular security audits and updates",
            "Data backup and disaster recovery procedures",
            "Employee training on data protection"
          ]
        }
      ]
    },
    {
      title: "International Data Transfers",
      icon: "Globe",
      content: [
        {
          subtitle: "Global Operations",
          items: [
            "Your data may be transferred to servers in different countries",
            "We ensure adequate protection through standard contractual clauses",
            "Compliance with GDPR and other international privacy laws",
            "Data processing agreements with third-party providers"
          ]
        }
      ]
    },
    {
      title: "Children\'s Privacy",
      icon: "Users",
      content: [
        {
          subtitle: "Age Restrictions",
          items: [
            "Our services are not intended for users under 13 years old",
            "We do not knowingly collect data from children",
            "Parents can request deletion of their child\'s information",
            "Age verification may be required for certain features"
          ]
        }
      ]
    },
    {
      title: "Cookies and Tracking",
      icon: "Cookie",
      content: [
        {
          subtitle: "Cookie Usage",
          items: [
            "Essential cookies for platform functionality",
            "Analytics cookies to understand user behavior",
            "Preference cookies to remember your settings",
            "You can manage cookie preferences in your browser"
          ]
        }
      ]
    },
    {
      title: "Data Retention",
      icon: "Archive",
      content: [
        {
          subtitle: "Retention Periods",
          items: [
            "Account data retained while your account is active",
            "Transaction records kept for legal compliance periods",
            "Analytics data aggregated and anonymized after 2 years",
            "Deleted account data removed within 30 days"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-blue-600 text-white">
        <div className="container mx-auto px-6 py-16">
          <Link
            to="/"
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} className="mr-2" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex items-center mb-4">
              <Icon name="Shield" size={48} className="mr-4" />
              <h1 className="text-4xl lg:text-5xl font-bold">
                Privacy Policy
              </h1>
            </div>
            <p className="text-xl text-white/90 mb-6">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
            <div className="flex items-center text-white/80">
              <Icon name="Calendar" size={16} className="mr-2" />
              <span className="text-sm">Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
          
          {/* Vertical Table of Contents (Desktop) */}
          <div className="hidden lg:block w-72 sticky top-8 shrink-0 bg-white rounded-2xl shadow-lg p-6 border border-slate-100">
            <div className="flex items-center mb-4 text-slate-800">
              <Icon name="List" size={20} className="mr-2 text-primary" />
              <h3 className="text-lg font-bold">Contents</h3>
            </div>
            <nav className="flex flex-col space-y-3">
              <a
                href="#introduction"
                className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
              >
                Introduction
              </a>
              {sections?.map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
                >
                  {section?.title}
                </a>
              ))}
              <a
                href="#contact"
                className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
              >
                Questions & Contact
              </a>
            </nav>
          </div>

          {/* Policy Sections */}
          <div className="flex-1 max-w-4xl w-full">
            {/* Mobile TOC */}
            <div className="lg:hidden bg-white rounded-2xl shadow-sm p-6 mb-8 border border-slate-100">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Contents</h3>
              <div className="flex flex-wrap gap-3">
                <a href="#introduction" className="text-sm bg-slate-50 px-3 py-1.5 rounded-full text-slate-600">Introduction</a>
                {sections?.map((section, index) => (
                  <a key={index} href={`#section-${index}`} className="text-sm bg-slate-50 px-3 py-1.5 rounded-full text-slate-600">
                    {section?.title}
                  </a>
                ))}
              </div>
            </div>

            {/* Introduction */}
            <div id="introduction" className="bg-white rounded-2xl shadow-lg p-8 mb-8 scroll-mt-24">
              <div className="flex items-start mb-6">
                <div className="bg-primary/10 rounded-xl p-3 mr-4">
                  <Icon name="Info" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-3">
                    Introduction
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    OpenGadget ("we," "our," or "us") is committed to protecting your privacy and ensuring you have a positive experience on our platform. This Privacy Policy describes how we collect, use, share, and protect your personal information when you use our 3D model catalog and visualization services.
                  </p>
                  <p className="text-slate-600 leading-relaxed mt-4">
                    By using OpenGadget, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                  </p>
                </div>
              </div>
            </div>

            {/* Policy Sections Iteration */}
            {sections?.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                id={`section-${sectionIndex}`}
                className="bg-white rounded-2xl shadow-lg p-8 mb-8 scroll-mt-24"
              >
                <div className="flex items-start mb-6">
                  <div className="bg-primary/10 rounded-xl p-3 mr-4">
                    <Icon name={section?.icon} size={24} className="text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    {section?.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section?.content?.map((subsection, subIndex) => (
                    <div key={subIndex}>
                      {subsection?.subtitle && (
                        <h3 className="text-lg font-semibold text-slate-700 mb-3">
                          {subsection?.subtitle}
                        </h3>
                      )}
                      <ul className="space-y-2">
                        {subsection?.items?.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start">
                            <Icon
                              name="Check"
                              size={20}
                              className="text-primary mr-3 mt-0.5 flex-shrink-0"
                            />
                            <span className="text-slate-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Contact and GDPR */}
            <div id="contact" className="bg-gradient-to-br from-primary to-blue-600 rounded-2xl shadow-lg p-8 text-white mb-8 scroll-mt-24">
            <div className="flex items-start mb-6">
              <Icon name="Mail" size={32} className="mr-4" />
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  Questions About Your Privacy?
                </h2>
                <p className="text-white/90 mb-6">
                  If you have any questions or concerns about this Privacy Policy or our data practices, please don't hesitate to contact us.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center">
                    <Icon name="Mail" size={18} className="mr-3" />
                    <span>privacy@opengadget.com</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="MapPin" size={18} className="mr-3" />
                    <span>Data Protection Officer, OpenGadget Inc.</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button
                    variant="secondary"
                    iconName="FileText"
                    iconPosition="left"
                    onClick={() => window.location.href = '/gdpr-compliance-center'}
                  >
                    GDPR Compliance Center
                  </Button>
                  <Button
                    variant="secondary"
                    iconName="Cookie"
                    iconPosition="left"
                    onClick={() => window.location.href = '/cookie-policy'}
                  >
                    Cookie Policy
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Changes */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-start">
              <div className="bg-amber-100 rounded-lg p-2 mr-4">
                <Icon name="AlertCircle" size={24} className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Changes to This Privacy Policy
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                </p>
              </div>
            </div>
          </div>
          
          </div> {/* End flex-1 wrapper */}

        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Explore 3D Models?
            </h3>
            <p className="text-slate-400 mb-8">
              Browse our catalog of high-quality 3D models with confidence, knowing your privacy is protected.
            </p>
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => window.location.href = '/model-catalog'}
            >
              Browse Model Catalog
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;